'use client';

/**
 * Template Selector - Choose from pre-defined entity templates
 */

import { useState } from 'react';
import {
  ENTITY_TEMPLATES,
  TEMPLATE_CATEGORIES,
  type EntityTemplate,
} from '@/lib/command-center/entity-templates';

interface TemplateSelectorProps {
  onSelect: (template: EntityTemplate | null) => void;
  onSkip: () => void;
}

export default function TemplateSelector({ onSelect, onSkip }: TemplateSelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof TEMPLATE_CATEGORIES | null>(null);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);

  const categories = Object.entries(TEMPLATE_CATEGORIES) as [keyof typeof TEMPLATE_CATEGORIES, { label: string; icon: string }][];

  const filteredTemplates = selectedCategory
    ? ENTITY_TEMPLATES.filter(t => t.category === selectedCategory)
    : ENTITY_TEMPLATES;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground">Choose a Template</h3>
        <p className="text-foreground-tertiary text-sm mt-1">
          Start with a template or create a blank entity
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
            selectedCategory === null
              ? 'bg-accent text-accent-foreground font-medium'
              : 'bg-cream/10 text-foreground-secondary hover:bg-cream/20'
          }`}
        >
          All
        </button>
        {categories.map(([key, { label, icon }]) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1.5 ${
              selectedCategory === key
                ? 'bg-accent text-accent-foreground font-medium'
                : 'bg-cream/10 text-foreground-secondary hover:bg-cream/20'
            }`}
          >
            <span>{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
        {filteredTemplates.map((template) => (
          <button
            key={template.id}
            onClick={() => onSelect(template)}
            onMouseEnter={() => setHoveredTemplate(template.id)}
            onMouseLeave={() => setHoveredTemplate(null)}
            className={`text-left p-3 rounded-lg border transition-all ${
              hoveredTemplate === template.id
                ? 'border-accent bg-accent-muted'
                : 'border-border hover:border-border/40'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">{template.icon}</span>
              <div className="min-w-0">
                <div className="font-medium text-foreground truncate">{template.name}</div>
                <div className="text-xs text-foreground-tertiary mt-0.5 line-clamp-2">
                  {template.description}
                </div>
                {template.suggestedTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.suggestedTags.slice(0, 2).map(tag => (
                      <span
                        key={tag}
                        className="px-1.5 py-0.5 bg-background text-foreground-muted text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Skip Option */}
      <div className="text-center pt-2 border-t border-border-muted">
        <button
          onClick={onSkip}
          className="text-foreground-tertiary hover:text-foreground text-sm transition-colors"
        >
          Skip and create blank entity
        </button>
      </div>
    </div>
  );
}
